/**
 * Single place that decides overlay vs. compact column + `appParams` for the messenger MFE.
 * If you change global stacking for compact MC, sync with messenger-front `consts/erpCompactMessenger.ts`
 * (`ERP_COMPACT_NAV_*`) so the flyout never paints above the OMS top bar.
 */
import { EMessengerScreenName, type IMessengerChatSource, type MessengerEventBus } from '@xometry/ui';
import type { RootStore } from 'stores/RootStore';
import { ESideOverlayType, type TSideOverlayContext } from 'stores/SideOverlay/SideOverlayStore';

import {
  buildErpMainMessengerMicroFrontendProps,
  type TErpMainMessengerHostAppParams,
} from './buildErpMainMessengerMicroFrontendProps';
import { getMessengerLayoutForRendering } from './getMessengerLayoutForRendering';
import {
  getMessengerOverlayChatSource,
  shouldSyncMessengerOverlayWithDealUrl,
} from './shouldSyncMessengerOverlayWithDealUrl';

type AmplitudeLoggerFn = (args: { event: string; attrs?: Record<string, string> }) => void;

export interface ErpMainMessengerHostComputed {
  showOverlayMessenger: boolean;
  showCompactColumnMessenger: boolean;
  appParams: TErpMainMessengerHostAppParams | null;
  microFrontendKey: string;
  renderLayout: ReturnType<typeof getMessengerLayoutForRendering>;
}

interface ComputeHostStateOpts {
  isMobile: boolean;
  isEmbedded: boolean;
  setEventBusRef: (eventBus: MessengerEventBus) => MessengerEventBus;
  amplitudeLogger: AmplitudeLoggerFn;
  /**
   * Current document thread from the deal URL (shipping / PO / …). Overlay `context.initialScreen` is not updated
   * when the user navigates while Message Center stays open in classic layout — compact column would otherwise
   * remount with stale `chatSource` after compact ↔ full toggles.
   */
  liveDealThreadChatSource?: IMessengerChatSource | null;
}

export const computeErpMainMessengerHostState = (
  rootStore: RootStore,
  opts: ComputeHostStateOpts,
): ErpMainMessengerHostComputed => {
  const { sideOverlayStore, erpMessengerLayoutStore } = rootStore;
  const { isMobile, isEmbedded, setEventBusRef, amplitudeLogger, liveDealThreadChatSource } = opts;

  const defaultReturn: ErpMainMessengerHostComputed = {
    showOverlayMessenger: false,
    showCompactColumnMessenger: false,
    appParams: null,
    microFrontendKey: 'messenger-closed',
    renderLayout: 'classic',
  };

  if (!sideOverlayStore.isOpen || sideOverlayStore.context.type !== ESideOverlayType.Messenger) return defaultReturn;

  const context = sideOverlayStore.context;

  const renderLayout = getMessengerLayoutForRendering({
    userPreference: erpMessengerLayoutStore.userErpMessengerLayoutMode,
    isMobile,
    isEmbedded,
    overlayContext: context,
  });

  const showCompactColumnMessenger = renderLayout === 'compact' && !isMobile && !isEmbedded;
  const showOverlayMessenger = !showCompactColumnMessenger;

  const onRequestClassicLayout =
    renderLayout === 'compact' ? rootStore.erpMessengerLayoutStore.requestClassicMessengerLayout : undefined;

  type MessengerOverlayContext = Extract<TSideOverlayContext, { type: ESideOverlayType.Messenger }>;

  let contextForBuild: MessengerOverlayContext = context;

  if (
    liveDealThreadChatSource &&
    shouldSyncMessengerOverlayWithDealUrl(context) &&
    liveDealThreadChatSource.sourceType === getMessengerOverlayChatSource(context)?.sourceType
  ) {
    const { type, launchContext, messengerLayoutPresentation } = context;

    contextForBuild = {
      type,
      launchContext,
      ...(messengerLayoutPresentation != null ? { messengerLayoutPresentation } : {}),
      initialScreen: {
        name: EMessengerScreenName.CHAT,
        params: {
          chatSource: liveDealThreadChatSource,
        },
      },
    };
  }

  /** MFE props: `erpMessengerLayoutMode` / optional `onRequestClassicLayout` — see `buildErpMainMessengerMicroFrontendProps` module doc. */
  const appParams = buildErpMainMessengerMicroFrontendProps({
    context: contextForBuild,
    setEventBusRef,
    amplitudeLogger,
    isMobile,
    erpMessengerLayoutMode: renderLayout,
    onRequestClassicLayout,
  });

  const microFrontendKey = [
    'messenger',
    showCompactColumnMessenger ? 'compact' : 'overlay',
    context.initialScreen ? JSON.stringify(context.initialScreen.name) : '',
    context.initialMessageId ?? '',
  ].join('-');

  return {
    showOverlayMessenger,
    showCompactColumnMessenger,
    appParams,
    microFrontendKey,
    renderLayout,
  };
};
